import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScrollLock } from '../lib/scroll-lock';
import { Link, useNavigate } from 'react-router-dom';
import { X, AlertCircle, CreditCard, Tag, CheckCircle2, Loader2, Users, Plus } from 'lucide-react';

// Matches MAX_GROUP_SEATS in src/server/routes/checkout.js.
const MAX_GROUP_SEATS = 25;

/**
 * Single-course checkout modal. Handles both guest + logged-in flows, for one
 * person or a group (one buyer paying for several attendees).
 *
 * Synchronous: hits /api/checkout/{initiate|guest} with card data, gets an
 * approve/decline back in the same request, and shows success or error inline.
 */
export default function CheckoutModal({ course, currentUser, onClose, onSuccess }) {
  const navigate = useNavigate();
  const isLoggedIn = !!currentUser;

  // ── Customer fields ──
  const [firstName, setFirstName] = useState(() => splitName(currentUser?.full_name).first);
  const [lastName,  setLastName]  = useState(() => splitName(currentUser?.full_name).last);
  const [email,     setEmail]     = useState(currentUser?.email || '');
  const [phone,     setPhone]     = useState('');
  // Hybrid events split their audience between Zoom and the venue. Default to
  // virtual so the field is never silently blank; the server defaults the same
  // way if it somehow arrives unset.
  const isHybrid = course.course_type === 'hybrid';
  // Seats left comes from the public course payload. `null` capacity means no
  // limit; 0 left means the room is full and only the online option is offered.
  const seatsLeft = course.in_person_seats_left;
  const inPersonFull = isHybrid && course.in_person_capacity != null && seatsLeft <= 0;
  // Hybrid buyers must actively pick; there's deliberately no default.
  const [attendanceMode, setAttendanceMode] = useState(null);
  const [attendanceMissing, setAttendanceMissing] = useState(false);
  const attendanceRef = useRef(null);
  const clearAttendanceError = () => {
    setAttendanceMissing(false);
    setError((e) => (e === 'Choose an attendance type.' ? null : e));
  };

  // ── Group registration ──
  // The buyer pays for everyone in `attendees`, plus themselves when
  // buyerAttending. Rows the server rejects are flagged by email.
  const [groupMode, setGroupMode] = useState(false);
  const [orgName, setOrgName] = useState('');
  const [buyerAttending, setBuyerAttending] = useState(false);
  const [attendees, setAttendees] = useState(() => [blankAttendee()]);
  const [flaggedEmails, setFlaggedEmails] = useState([]);
  const [modesMissing, setModesMissing] = useState(false);
  const seatCount = groupMode ? attendees.length + (buyerAttending ? 1 : 0) : 1;
  const buyerChoosesMode = isHybrid && (!groupMode || buyerAttending);
  const updateAttendee = (key, patch) => {
    setAttendees((list) => list.map((a) => (a.key === key ? { ...a, ...patch } : a)));
  };
  const addAttendee = () => setAttendees((list) => [...list, blankAttendee()]);
  const removeAttendee = (key) => setAttendees((list) => list.filter((a) => a.key !== key));

  // ── Discount ──
  const [discountCode, setDiscountCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(null); // { discount_code, attendance_mode, subtotal_cents, discount_cents, final_cents }
  const [discountStatus, setDiscountStatus] = useState('idle'); // idle | checking | applied | error
  const [discountError, setDiscountError] = useState(null);

  // ── Card fields ──
  const [cardNumber, setCardNumber] = useState('');
  const [expMonth,   setExpMonth]   = useState('');
  const [expYear,    setExpYear]    = useState('');
  const [cvv,        setCvv]        = useState('');
  const [zipCode,    setZipCode]    = useState('');

  // ── Flow ──
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [loginRequired, setLoginRequired] = useState(false);
  const [success, setSuccess] = useState(null); // { isGuest, purchase_id, free }
  const firstFieldRef = useRef(null);

  // Freeze the page behind the modal
  useScrollLock();

  // Esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  useEffect(() => {
    firstFieldRef.current?.focus();
  }, []);

  // Discounts apply per person. A code limited to online or in-person seats
  // covers only those seats (mirrors codeCoversSeat in checkout.js); a hybrid
  // seat counts once its attendance type is picked.
  const codeMode  = discountApplied?.attendance_mode || null;
  const seatModes = groupMode
    ? [...(buyerAttending ? [attendanceMode] : []), ...attendees.map((a) => a.mode)]
    : [attendanceMode];
  const coversSeat = (mode) => !codeMode || (isHybrid ? mode === codeMode : codeMode === 'virtual');
  const discountedSeats = discountApplied ? seatModes.filter(coversSeat).length : 0;
  const codeModeWord = codeMode === 'virtual' ? 'online' : 'in-person';
  // Every seat has picked, and the code covers none of them.
  const codeCoversNoSeat = !!discountApplied && !discountedSeats && seatModes.every(Boolean);
  const subtotal = course.price_cents * seatCount;
  const discount = (discountApplied?.discount_cents ?? 0) * discountedSeats;
  const total    = subtotal - discount;
  const requiresCard = total > 0;

  const applyDiscount = async () => {
    setDiscountError(null);
    setDiscountStatus('checking');
    try {
      const res = await fetch('/api/checkout/preview-discount', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ course_id: course.id, discount_code: discountCode.trim() }),
      });
      const data = await res.json();
      if (!res.ok) {
        setDiscountStatus('error');
        setDiscountError(discountErrorMessage(data.reason || data.error));
        setDiscountApplied(null);
        return;
      }
      setDiscountApplied(data);
      setDiscountStatus('applied');
    } catch {
      setDiscountStatus('error');
      setDiscountError('Network error. Try again.');
    }
  };

  const removeDiscount = () => {
    setDiscountApplied(null);
    setDiscountStatus('idle');
    setDiscountError(null);
    setDiscountCode('');
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoginRequired(false);
    setFlaggedEmails([]);
    if (buyerChoosesMode && !attendanceMode) {
      setAttendanceMissing(true);
      setError('Choose an attendance type.');
      attendanceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    if (groupMode && isHybrid && attendees.some((a) => !a.mode)) {
      setModesMissing(true);
      setError('Choose in person or online for every attendee.');
      return;
    }
    if (codeCoversNoSeat) {
      setError(`${discountApplied.discount_code} is for ${codeModeWord} attendance only. Remove the code or change the attendance type.`);
      return;
    }
    setBusy(true);

    const card = requiresCard ? {
      cardNumber: cardNumber.replace(/\s+/g, ''),
      expMonth,
      expYear: expYear.length === 2 ? `20${expYear}` : expYear,
      cvv,
      firstName: firstName.trim(),
      lastName:  lastName.trim(),
      zipCode:   zipCode.trim(),
    } : undefined;

    const groupBody = groupMode ? {
      organization_name: orgName.trim() || undefined,
      buyer_attending: buyerAttending,
      ...(isHybrid && buyerAttending ? { buyer_attendance_mode: attendanceMode } : {}),
      attendees: attendees.map((a) => ({
        first_name: a.first.trim(),
        last_name:  a.last.trim(),
        email:      a.email.trim(),
        ...(isHybrid ? { attendance_mode: a.mode } : {}),
      })),
    } : null;

    const body = groupMode
      ? {
          course_id: course.id,
          discount_code: discountApplied?.discount_code,
          ...(isLoggedIn ? {} : { email: email.trim(), full_name: `${firstName.trim()} ${lastName.trim()}`.trim() }),
          ...groupBody,
          card,
        }
      : isLoggedIn
      ? {
          course_id: course.id,
          discount_code: discountApplied?.discount_code,
          ...(phone.trim() ? { phone: phone.trim() } : {}),
          ...(isHybrid ? { attendance_mode: attendanceMode } : {}),
          card,
        }
      : {
          course_id: course.id,
          email: email.trim(),
          full_name: `${firstName.trim()} ${lastName.trim()}`.trim(),
          discount_code: discountApplied?.discount_code,
          ...(isHybrid ? { attendance_mode: attendanceMode } : {}),
          card,
        };

    try {
      const endpoint = groupMode
        ? (isLoggedIn ? '/api/checkout/group' : '/api/checkout/group/guest')
        : (isLoggedIn ? '/api/checkout/initiate' : '/api/checkout/guest');
      const res = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();

      if (res.status === 409 && data.error === 'login_required') {
        setLoginRequired(true);
        return;
      }
      if (data.error === 'attendees_already_enrolled' || data.error === 'duplicate_attendees') {
        setFlaggedEmails(data.emails || []);
        setError(data.error === 'duplicate_attendees'
          ? `Each person needs their own email address. Listed more than once: ${(data.emails || []).join(', ')}.`
          : `Already registered for this course: ${(data.emails || []).join(', ')}. Remove them and try again.`);
        return;
      }
      if (groupMode && res.status === 409 && data.error === 'in_person_full') {
        setError(data.message);
        return;
      }
      if (res.status === 409 && data.error === 'in_person_full') {
        setAttendanceMode('virtual');
        setError(data.message || 'In-person seats just sold out. Switched you to online — submit again to confirm.');
        return;
      }
      if (res.status === 409 && data.error === 'already_enrolled') {
        onSuccess?.({ enrollmentId: data.enrollment_id, alreadyEnrolled: true });
        return;
      }
      if (res.status === 402 && data.declined) {
        setError(declineMessage(data.reason));
        return;
      }
      if (!res.ok) {
        setError(data.message || prettyError(data.error, data.reason, data.fields));
        return;
      }

      // Guests come back signed in to a provisional account. Store it now, but
      // show the confirmation before moving them on to the dashboard.
      if (!isLoggedIn && data.user) {
        localStorage.setItem('learnable_user', JSON.stringify(data.user));
      }

      if (groupMode) {
        setSuccess({
          group: true,
          free: !!data.free,
          seats: data.seats || [],
          buyerEmail: data.buyer_email,
          buyerAttending: !!data.buyer_attending,
        });
        return;
      }

      setSuccess({
        isGuest: !isLoggedIn,
        purchaseId: data.purchase_id,
        enrollmentId: data.enrollment_id,
        free: !!data.free,
      });
    } catch {
      setError('Network error. Try again.');
    } finally {
      setBusy(false);
    }
  };

  // ── Render success state ──
  if (success) {
    const finishGuest = () => { onClose(); navigate('/dashboard'); };
    return (
      <ModalShell onClose={(success.group ? success.buyerAttending : success.isGuest) ? finishGuest : onClose}>
        <div className="p-8 text-center overflow-y-auto">
          <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
            <CheckCircle2 size={32} className="text-emerald-600" />
          </div>
          <h2 className="text-2xl font-bold mb-2">{success.group ? 'Registration complete!' : "You're enrolled!"}</h2>
          {success.group ? (
            <>
              {!success.free && (
                <p className="mb-3 font-semibold text-emerald-700">Your payment was successful.</p>
              )}
              <p className="text-slate-600">
                A receipt has been sent to <strong>{success.buyerEmail}</strong>.
              </p>
              <p className="mt-3 text-slate-600">
                Each attendee will receive an email at the address you entered with instructions for
                accessing the course. Attendees who are new to Learnable will be asked to set up their
                account and create a password. (If an email doesn't arrive, have them check alternate
                email folders.)
              </p>
              <ul className="mt-4 text-left text-sm border border-slate-200 rounded-lg divide-y divide-slate-100">
                {success.seats.map((s) => (
                  <li key={s.email} className="px-3 py-2 flex items-center justify-between gap-3">
                    <span className="min-w-0">
                      <span className="block font-medium text-slate-900 truncate">{s.full_name}</span>
                      <span className="block text-xs text-slate-500 truncate">{s.email}</span>
                    </span>
                    {s.attendance_mode && (
                      <span className="text-xs text-slate-500 shrink-0">
                        {s.attendance_mode === 'in_person' ? 'In person' : 'Online'}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              <button
                onClick={success.buyerAttending ? finishGuest : onClose}
                className="mt-6 bg-brand text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-brand-dark"
              >
                Done
              </button>
            </>
          ) : success.isGuest ? (
            <>
              {!success.free && (
                <p className="mb-3 font-semibold text-emerald-700">Your payment was successful.</p>
              )}
              <p className="text-slate-600">
                You have been sent an email at <strong>{email}</strong> to complete the setup of
                your Learnable account. Please open the email and follow the instructions to verify
                your email address and create a password. (If you do not receive the email, be sure
                to check alternate email folders.)
              </p>
              <p className="mt-3 text-slate-600">
                Once your account is set up, you will have full access to the course, including the
                course roadmap and instructions for attending either in person or virtually, based
                on the specific course or the attendance option you selected.
              </p>
              <button
                onClick={finishGuest}
                className="mt-6 bg-brand text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-brand-dark"
              >
                Done
              </button>
            </>
          ) : (
            <>
              <p className="text-slate-600">
                {success.free ? 'Access is available now.' : 'Payment received. Access is available now.'}
              </p>
              <Link
                to="/dashboard"
                className="mt-6 inline-block bg-brand text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-brand-dark"
              >
                Go to dashboard
              </Link>
            </>
          )}
        </div>
      </ModalShell>
    );
  }

  // ── Render login-required state ──
  if (loginRequired) {
    return (
      <ModalShell onClose={onClose}>
        <div className="p-8">
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-5">
            <h3 className="font-semibold text-amber-900 flex items-center gap-2">
              <AlertCircle size={16} /> You already have an account
            </h3>
            <p className="text-sm text-amber-800 mt-1">
              An account exists for <strong>{email}</strong>. Log in to complete your purchase.
            </p>
            <div className="mt-3 flex gap-3">
              <Link
                to={`/login?next=/courses/${course.slug}`}
                className="bg-amber-600 text-white text-sm px-4 py-2 rounded-md font-medium hover:bg-amber-700"
              >
                Log in
              </Link>
              <button
                onClick={() => setLoginRequired(false)}
                className="text-sm text-amber-800 hover:underline"
              >
                Use a different email
              </button>
            </div>
          </div>
        </div>
      </ModalShell>
    );
  }

  // How the buyer will attend (hybrid events only).
  const attendanceSection = (
    <div ref={attendanceRef}>
      <Section title="How will you attend?">
        <div className={`grid grid-cols-2 gap-3 rounded-lg ${attendanceMissing ? 'ring-2 ring-rose-400 ring-offset-2' : ''}`}>
          <AttendanceModeButton
            active={attendanceMode === 'in_person'}
            disabled={inPersonFull}
            label="In person"
            sublabel={inPersonFull
              ? 'Sold out'
              : (seatsLeft != null && seatsLeft <= 5
                  ? `${seatsLeft} seat${seatsLeft === 1 ? '' : 's'} left`
                  : (course.venue_name || 'At the venue'))}
            onClick={() => { if (!inPersonFull) { setAttendanceMode('in_person'); clearAttendanceError(); } }}
          />
          <AttendanceModeButton
            active={attendanceMode === 'virtual'}
            label="Online"
            sublabel="Join by Zoom"
            onClick={() => { setAttendanceMode('virtual'); clearAttendanceError(); }}
          />
        </div>
        {attendanceMissing && (
          <p className="-mt-1 text-xs font-semibold text-rose-600">Choose an attendance type.</p>
        )}
        <p className="-mt-1 text-xs text-slate-500">
          {inPersonFull
            ? 'In-person seats are sold out — you can still join online.'
            : "Need to switch later? Contact us and we'll update it."}
        </p>
      </Section>
    </div>
  );

  // ── Render main form ──
  return (
    <ModalShell onClose={busy ? () => {} : onClose}>
      <form onSubmit={submit} className="flex flex-col min-h-0">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-white shrink-0">
          <h2 className="text-lg font-bold">Checkout</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-slate-400 hover:text-slate-700 disabled:opacity-40"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-5">
          {/* Course summary */}
          <div className="flex items-start justify-between gap-4 pb-4 border-b border-slate-200">
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">Course</div>
              <div className="font-semibold text-slate-900 truncate">{course.title}</div>
            </div>
            <div className="text-right shrink-0">
              <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                {groupMode ? 'Per person' : 'Price'}
              </div>
              <div className="font-bold text-slate-900">{fmt(course.price_cents)}</div>
            </div>
          </div>

          {/* Who's being registered */}
          <Section title={<><Users size={14} className="inline -mt-0.5 mr-1" />Who are you registering?</>}>
            <div className="grid grid-cols-2 gap-3">
              <AttendanceModeButton
                active={!groupMode}
                label="Just me"
                sublabel="One registration"
                onClick={() => { setGroupMode(false); setError(null); }}
              />
              <AttendanceModeButton
                active={groupMode}
                label="Others or a group"
                sublabel={`Pay for up to ${MAX_GROUP_SEATS} people`}
                onClick={() => { setGroupMode(true); setError(null); }}
              />
            </div>
          </Section>

          {/* How they'll attend (hybrid; single checkout puts it first) */}
          {buyerChoosesMode && !groupMode && attendanceSection}

          {/* Account section (guest only) */}
          {!isLoggedIn && (
            <Section title="Your details">
              <Field label="Email">
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputCls}
                  placeholder="jane@example.com"
                  ref={firstFieldRef}
                />
              </Field>
              <p className="-mt-2 text-xs text-slate-500">
                {groupMode
                  ? "We'll send the receipt here."
                  : "We'll send your receipt and account-setup link here."}
              </p>
            </Section>
          )}

          {/* Name (cardholder + account name) */}
          <Section title={isLoggedIn ? 'Cardholder' : groupMode ? 'Your name' : 'Name'}>
            <div className="grid grid-cols-2 gap-3">
              <Field label="First name">
                <input
                  type="text"
                  required
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={inputCls}
                  maxLength={60}
                  ref={isLoggedIn ? firstFieldRef : undefined}
                />
              </Field>
              <Field label="Last name">
                <input
                  type="text"
                  required
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={inputCls}
                  maxLength={60}
                />
              </Field>
            </div>
          </Section>

          {/* Phone (logged-in users who haven't provided one yet) */}
          {isLoggedIn && !currentUser?.phone && (
            <>
              <Field label="Phone number (optional)">
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={inputCls}
                  placeholder="(555) 123-4567"
                  maxLength={30}
                />
              </Field>
              <span className="inline-block -mt-1 text-xs text-slate-600 bg-slate-100 rounded-full px-3 py-1">
                For course access issues only — we won't text you marketing.
              </span>
            </>
          )}

          {groupMode && (
            <>
              <Field label="Organization (optional)">
                <input
                  type="text"
                  autoComplete="organization"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className={inputCls}
                  maxLength={120}
                />
              </Field>
              <label className="flex items-start gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={buyerAttending}
                  onChange={(e) => setBuyerAttending(e.target.checked)}
                  className="mt-0.5 accent-brand"
                />
                <span>
                  <span className="font-semibold">I'm also attending</span>
                  <span className="block text-xs text-slate-500">
                    Register me too{isLoggedIn ? '' : ' — I\'ll get my own account-setup email'}.
                  </span>
                </span>
              </label>
              {buyerChoosesMode && attendanceSection}

              <Section title={`${buyerAttending ? 'Other attendees' : 'Attendees'} (${attendees.length})`}>
                {attendees.map((a, i) => {
                  const flagged = flaggedEmails.includes(a.email.trim().toLowerCase());
                  return (
                    <div
                      key={a.key}
                      className={`rounded-lg border p-3 space-y-3 ${flagged ? 'border-rose-300 bg-rose-50/50' : 'border-slate-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-slate-600">Attendee {i + 1}</span>
                        {attendees.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeAttendee(a.key)}
                            className="text-slate-400 hover:text-slate-700"
                            aria-label={`Remove attendee ${i + 1}`}
                          >
                            <X size={16} />
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Field label="First name">
                          <input
                            type="text"
                            required
                            value={a.first}
                            onChange={(e) => updateAttendee(a.key, { first: e.target.value })}
                            className={inputCls}
                            maxLength={60}
                          />
                        </Field>
                        <Field label="Last name">
                          <input
                            type="text"
                            required
                            value={a.last}
                            onChange={(e) => updateAttendee(a.key, { last: e.target.value })}
                            className={inputCls}
                            maxLength={60}
                          />
                        </Field>
                      </div>
                      <Field label="Email">
                        <input
                          type="email"
                          required
                          value={a.email}
                          onChange={(e) => updateAttendee(a.key, { email: e.target.value })}
                          className={inputCls}
                          maxLength={254}
                        />
                      </Field>
                      {isHybrid && (
                        <div className={`grid grid-cols-2 gap-2 rounded-lg ${modesMissing && !a.mode ? 'ring-2 ring-rose-400 ring-offset-2' : ''}`}>
                          <AttendanceModeButton
                            active={a.mode === 'in_person'}
                            disabled={inPersonFull}
                            label="In person"
                            sublabel={inPersonFull ? 'Sold out' : (course.venue_name || 'At the venue')}
                            onClick={() => { if (!inPersonFull) updateAttendee(a.key, { mode: 'in_person' }); }}
                          />
                          <AttendanceModeButton
                            active={a.mode === 'virtual'}
                            label="Online"
                            sublabel="Join by Zoom"
                            onClick={() => updateAttendee(a.key, { mode: 'virtual' })}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
                {seatCount < MAX_GROUP_SEATS ? (
                  <button
                    type="button"
                    onClick={addAttendee}
                    className="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline"
                  >
                    <Plus size={16} /> Add attendee
                  </button>
                ) : (
                  <p className="text-xs text-slate-500">
                    Up to {MAX_GROUP_SEATS} people per registration. For a larger group, contact us.
                  </p>
                )}
                <p className="text-xs text-slate-500">
                  Each attendee gets their own email with instructions for accessing the course.
                </p>
              </Section>
            </>
          )}

          {/* Discount code */}
          <Section title={<><Tag size={14} className="inline -mt-0.5 mr-1" />Discount code (optional)</>}>
            {discountApplied ? (
              <div className="flex items-center justify-between px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-md">
                <div className="text-sm">
                  <span className="font-bold text-emerald-800">{discountApplied.discount_code}</span>
                  <span className="text-emerald-700">
                    {' '}applied · −{fmt(discountApplied.discount_cents)}{groupMode ? ' per person' : ''}
                    {isHybrid && codeMode && ` · ${codeModeWord} attendance only`}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={removeDiscount}
                  className="text-xs text-emerald-800 hover:underline"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => { setDiscountCode(e.target.value); setDiscountError(null); }}
                  className={inputCls + ' uppercase'}
                  placeholder="ENTER CODE"
                  maxLength={60}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                />
                <button
                  type="button"
                  onClick={applyDiscount}
                  disabled={!discountCode.trim() || discountStatus === 'checking'}
                  className="px-4 py-2 text-sm font-semibold border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
                >
                  {discountStatus === 'checking' ? '…' : 'Apply'}
                </button>
              </div>
            )}
            {discountError && (
              <p className="text-xs text-rose-600 mt-1">{discountError}</p>
            )}
            {isHybrid && codeMode && seatModes.some((m) => m && !coversSeat(m)) && (
              <p className="text-xs text-amber-700 mt-1">
                {groupMode
                  ? `This code is for ${codeModeWord} attendance only, so ${discountedSeats} of ${seatCount} attendees get the discount.`
                  : `This code is for ${codeModeWord} attendance only, so it doesn't apply to ${codeMode === 'virtual' ? 'an in-person' : 'an online'} seat.`}
              </p>
            )}
          </Section>

          {/* Card section (only when total > 0) */}
          {requiresCard && (
            <Section title={<><CreditCard size={14} className="inline -mt-0.5 mr-1" />Card</>}>
              <Field label="Card number">
                <input
                  type="text"
                  required
                  inputMode="numeric"
                  autoComplete="cc-number"
                  value={cardNumber}
                  onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                  className={inputCls + ' font-mono tracking-wider'}
                  placeholder="1234 5678 9012 3456"
                  maxLength={23}
                />
              </Field>
              <div className="grid grid-cols-3 gap-3">
                <Field label="Exp. month">
                  <input
                    type="text"
                    required
                    inputMode="numeric"
                    autoComplete="cc-exp-month"
                    value={expMonth}
                    onChange={(e) => setExpMonth(e.target.value.replace(/\D/g, '').slice(0, 2))}
                    className={inputCls}
                    placeholder="MM"
                    maxLength={2}
                  />
                </Field>
                <Field label="Exp. year">
                  <input
                    type="text"
                    required
                    inputMode="numeric"
                    autoComplete="cc-exp-year"
                    value={expYear}
                    onChange={(e) => setExpYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    className={inputCls}
                    placeholder="YYYY"
                    maxLength={4}
                  />
                </Field>
                <Field label="CVV">
                  <input
                    type="text"
                    required
                    inputMode="numeric"
                    autoComplete="cc-csc"
                    value={cvv}
                    onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    className={inputCls}
                    placeholder="123"
                    maxLength={4}
                  />
                </Field>
              </div>
              <Field label="Billing ZIP">
                <input
                  type="text"
                  required
                  inputMode="numeric"
                  autoComplete="postal-code"
                  value={zipCode}
                  onChange={(e) => setZipCode(e.target.value)}
                  className={inputCls}
                  placeholder="12345"
                  maxLength={15}
                />
              </Field>
            </Section>
          )}

          {/* Totals */}
          <div className="pt-4 border-t border-slate-200 space-y-1 text-sm">
            <div className="flex justify-between text-slate-600">
              <span>Subtotal{groupMode ? ` (${seatCount} × ${fmt(course.price_cents)})` : ''}</span>
              <span>{fmt(subtotal)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-emerald-700">
                <span>Discount</span><span>−{fmt(discount)}</span>
              </div>
            )}
            <div className="flex justify-between font-bold text-lg pt-2 border-t border-slate-100">
              <span>Total</span><span>{fmt(total)}</span>
            </div>
          </div>
        </div>

        {/* Pinned above the Pay button, outside the scrolling body: at the end
            of the body it rendered below the fold, so a decline looked like
            nothing happened. */}
        {error && (
          <div role="alert" className="px-6 py-3 border-t border-rose-200 bg-rose-50 text-rose-700 text-sm flex items-start gap-2 shrink-0">
            <AlertCircle size={16} className="shrink-0 mt-0.5" /> {error}
          </div>
        )}

        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 shrink-0 flex items-center justify-between gap-3">
          <p className="text-[11px] text-slate-500 leading-snug">
            Payments processed securely by PayJunction. We don't store your card details.
          </p>
          <button
            type="submit"
            disabled={busy}
            className="bg-brand text-white px-5 py-2.5 rounded-lg font-semibold hover:bg-brand-dark disabled:opacity-50 inline-flex items-center gap-2 shrink-0"
          >
            {busy && <Loader2 size={16} className="animate-spin" />}
            {busy ? 'Processing…' : total === 0 ? 'Enroll' : `Pay ${fmt(total)}`}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ── Sub-components ───────────────────────────────────────
function ModalShell({ children, onClose }) {
  // Render into document.body so the fixed overlay always resolves against the
  // viewport — escaping any ancestor with a transform/filter/overflow (e.g. the
  // animated LiveSessionModal box on the Home page), which would otherwise trap
  // and clip this modal.
  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-slate-900/60 flex items-start sm:items-center justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[calc(100vh-2rem)] flex flex-col overflow-hidden">
        {children}
      </div>
    </div>,
    document.body
  );
}

function AttendanceModeButton({ active, disabled, label, sublabel, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
      className={`p-3 rounded-lg border-2 text-left transition ${
        disabled
          ? 'border-slate-200 bg-slate-50 opacity-60 cursor-not-allowed'
          : active
            ? 'border-brand bg-brand/5'
            : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      <div className={`text-sm font-semibold ${active && !disabled ? 'text-brand' : 'text-slate-700'}`}>{label}</div>
      <div className="text-xs text-slate-500 truncate">{sublabel}</div>
    </button>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h3 className="text-xs font-bold uppercase tracking-widest text-slate-500">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-semibold text-slate-600 mb-1">{label}</span>
      {children}
    </label>
  );
}

const inputCls =
  'w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand';

// ── Helpers ──────────────────────────────────────────────
function fmt(cents) {
  return cents === 0 ? 'Free' : `$${(cents / 100).toFixed(2)}`;
}

function formatCardNumber(s) {
  const digits = s.replace(/\D/g, '').slice(0, 19);
  return digits.replace(/(.{4})/g, '$1 ').trim();
}

let attendeeKey = 0;
function blankAttendee() {
  attendeeKey += 1;
  return { key: attendeeKey, first: '', last: '', email: '', mode: null };
}

function splitName(full) {
  if (!full) return { first: '', last: '' };
  const parts = full.trim().split(/\s+/);
  return {
    first: parts[0] || '',
    last:  parts.slice(1).join(' ') || '',
  };
}

const FIELD_LABELS = {
  'card.cardNumber': 'card number',
  'card.expMonth':   'expiration month',
  'card.expYear':    'expiration year',
  'card.cvv':        'CVV',
  'card.firstName':  'first name',
  'card.lastName':   'last name',
  'card.zipCode':    'billing ZIP',
  email:             'email',
  full_name:         'name',
  phone:             'phone',
  discount_code:     'discount code',
};

function prettyError(code, reason, fields) {
  if (code === 'card_required') return 'Card details are required.';
  if (code === 'attendance_mode_required') return 'Choose an attendance type.';
  if (code === 'invalid_input') {
    const names = (fields || []).map((f) => FIELD_LABELS[f]).filter(Boolean);
    return names.length
      ? `Please check your ${names.join(', ')} and try again.`
      : 'Some fields look wrong. Double-check and try again.';
  }
  if (code === 'discount_invalid') return discountErrorMessage(reason);
  if (code === 'payment_gateway_error') return "Couldn't reach the payment processor. Try again.";
  if (code === 'post_charge_failure') return 'Payment processed but enrollment failed. Contact support.';
  return 'Something went wrong. Try again.';
}

// PayRelay passes the processor's wording through ("Declined (do not honor)").
// Say plainly that the card was declined and what to try next.
function declineMessage(reason) {
  if (/AVS|zip|address/i.test(reason || '')) {
    return "Your card was declined: the billing ZIP doesn't match the card. Check the ZIP and try again, or use a different card.";
  }
  // "Declined (do not honor)" → "do not honor"; a bare "Declined" → nothing.
  const detail = (reason || '').replace(/^declined\s*/i, '').replace(/^\((.*)\)$/, '$1').trim();
  return `Your card was declined${detail ? ` (${detail})` : ''}. Try a different card or contact your bank.`;
}

function discountErrorMessage(reason) {
  switch (reason) {
    case 'not_found':       return "That code isn't valid.";
    case 'inactive':        return 'That code is no longer active.';
    case 'expired':         return 'That code has expired.';
    case 'limit_reached':   return 'That code has reached its usage limit.';
    case 'limit_reached_or_expired': return 'That code is no longer available.';
    case 'not_applicable':  return "That code doesn't apply to this course.";
    case 'below_minimum':   return 'Order does not meet the code minimum.';
    case 'already_used':    return "You've already used this code.";
    case 'not_enough_uses': return "That code doesn't have enough uses left for everyone in this registration.";
    case 'online_only':     return 'That code is for online attendance only.';
    case 'in_person_only':  return 'That code is for in-person attendance only.';
    default:                return 'Discount code invalid.';
  }
}
